import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { EndIlClassButtonComponent } from '@ilt/components/end-il-class-button/end-il-class-button.component';
import { TranslateModule } from '@ngx-translate/core';
import { VerifiableCredentialsComponent } from '@shared/components/verifiable-credentials/verifiable-credentials.component';
import { CustomFieldComponent } from '@user/components/custom-field/custom-field.component';
import { QRCodeModule } from 'angularx-qrcode';
import { ClipboardModule } from 'ngx-clipboard';
import { AppStoreLinksComponent } from '../components/shared/app-store-links/app-store-links.component';
import { TimezoneSelectComponent } from '../components/shared/timezone-select/timezone-select.component';
import { PrimeModule } from '../prime.module';
import { AblyChannelSubscriptionComponent } from './components/ably-channel-subscription/ably-channel-subscription.component';
import { ActionsMenuItemComponent } from './components/actions-menu-item/actions-menu-item.component';
import { ActionsMenuComponent } from './components/actions-menu/actions-menu.component';
import { AddLearnersStepComponent } from './components/add-learners-wizard/add-learners-step/add-learners-step.component';
import { AddLearnersWizardComponent } from './components/add-learners-wizard/add-learners-wizard.component';
import { ConfirmLearnersTableComponent } from './components/add-learners-wizard/confirm-learners-table/confirm-learners-table.component';
import { MaxLearnersMessageComponent } from './components/add-learners-wizard/max-learners-message/max-learners-message.component';
import { AddressFormComponent } from './components/address-form/address-form.component';
import { AddressComponent } from './components/address/address.component';
import { AppDistractorComponent } from './components/app-distractor/app-distractor.component';
import { AssignTrainingWizardComponent } from './components/assign-training-wizard/assign-training-wizard.component';
import { AssignTrainingComponent } from './components/assign-training/assign-training.component';
import { AvatarComponent } from './components/avatar/avatar.component';
import { BadgeComponent } from './components/badge/badge.component';
import { BatchActionsComponent } from './components/batch-actions/batch-actions.component';
import { BlockableComponent } from './components/blockable/blockable.component';
import { BulkDiscountFormComponent } from './components/bulk-discount-form/bulk-discount-form.component';
import { CancelIlBookingComponent } from './components/cancel-il-booking/cancel-il-booking.component';
import { CardTitleComponent } from './components/card-title/card-title.component';
import { CartItemComponent } from './components/cart-item/cart-item.component';
import { CertificationCardComponent } from './components/certification-card/certification-card.component';
import { CertificationChangeRequestComponent } from './components/certification-change-request/certification-change-request.component';
import { CertificationDetailsComponent } from './components/certification-details/certification-details.component';
import { CertificationExportComponent } from './components/certification-export/certification-export.component';
import { CertificationListComponent } from './components/certification-list/certification-list.component';
import { CheckoutBookableClassListComponent } from './components/checkout-bookable-class-list/checkout-bookable-class-list.component';
import { CheckoutBookableClassComponent } from './components/checkout-bookable-class/checkout-bookable-class.component';
import { CheckoutBookingCartItemComponent } from './components/checkout-booking-cart-item/checkout-booking-cart-item.component';
import { CheckoutBookingComponent } from './components/checkout-booking/checkout-booking.component';
import { CheckoutOrderComponent } from './components/checkout-order/checkout-order.component';
import { CheckoutReceiptComponent } from './components/checkout-receipt/checkout-receipt.component';
import { CheckoutWizardComponent } from './components/checkout-wizard/checkout-wizard.component';
import { ClaimPromoCodeComponent } from './components/claim-promo-code/claim-promo-code.component';
import { ColorPickerComponent } from './components/color-picker/color-picker.component';
import { ConfirmButtonComponent } from './components/confirm-button/confirm-button.component';
import { ContentHeaderComponent } from './components/content-header/content-header.component';
import { CopyButtonComponent } from './components/copy-button/copy-button.component';
import { CourseDeprecationBadgeComponent } from './components/course-deprecation-badge/course-deprecation-badge.component';
import { DashboardFavouriteReportsComponent } from './components/dashboard-favourite-reports/dashboard-favourite-reports.component';
import { DashboardTileComponent } from './components/dashboard-tile/dashboard-tile.component';
import { DateComponent } from './components/date/date.component';
import { DestroyNodeButtonComponent } from './components/destroy-node-button/destroy-node-button.component';
import { DialogToggleComponent } from './components/dialog-toggle/dialog-toggle.component';
import { DriverComponent } from './components/driver/driver.component';
import { EditButtonComponent } from './components/edit-button/edit-button.component';
import { EditFormComponent } from './components/edit-form/edit-form.component';
import { EmailAddressComponent } from './components/email-address/email-address.component';
import { EmptyMessageComponent } from './components/empty-message/empty-message.component';
import { ExternalLinkComponent } from './components/external-link/external-link.component';
import { FeatureGateComponent } from './components/feature-gate/feature-gate.component';
import { FileDisplayComponent } from './components/file-display/file-display.component';
import { FileListComponent } from './components/file-list/file-list.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { FlexContainerComponent } from './components/flex-container/flex-container.component';
import { FloatedLabelFieldComponent } from './components/floated-label-field/floated-label-field.component';
import { FormActionsComponent } from './components/form-actions/form-actions.component';
import { FormFieldComponent } from './components/form-field/form-field.component';
import { FormOptionComponent } from './components/form-option/form-option.component';
import { FormSectionComponent } from './components/form-section/form-section.component';
import { FormComponent } from './components/form/form.component';
import { GrantStatusComponent } from './components/grant-status/grant-status.component';
import { GridComponent } from './components/grid/grid.component';
import { HasManyPricesFormComponent } from './components/has-many-prices-form/has-many-prices-form.component';
import { HelpTipComponent } from './components/help-tip/help-tip.component';
import { IlBookingTableRowComponent } from './components/il-booking-cell/il-booking-table-row.component';
import { IlClassAvailabilityComponent } from './components/il-class-availability/il-class-availability.component';
import { IlClassCourseItemsComponent } from './components/il-class-course-items/il-class-course-items.component';
import { IlClassEnrolCardComponent } from './components/il-class-enrol-card/il-class-enrol-card.component';
import { IlClassInstructorComponent } from './components/il-class-instructor/il-class-instructor.component';
import { IlClassResultsSummaryComponent } from './components/il-class-results-summary/il-class-results-summary.component';
import { IlClassSessionComponent } from './components/il-class-session/il-class-session.component';
import { IlClassSessionsCellComponent } from './components/il-class-sessions-cell/il-class-sessions-cell.component';
import { IlClassStatusComponent } from './components/il-class-status/il-class-status.component';
import { IlCourseClassListComponent } from './components/il-course-class-list/il-course-class-list.component';
import { IlSessionLocationComponent } from './components/il-session-location/il-session-location.component';
import { InviteStatusComponent } from './components/invite-status/invite-status.component';
import { KeyValueFormComponent } from './components/key-value-form/key-value-form.component';
import { LearnersImportComponent } from './components/learners-import/learners-import.component';
import { LinkDocumentButtonComponent } from './components/link-document-button/link-document-button.component';
import { ListFilterComponent } from './components/list-filter/list-filter.component';
import { LvServiceComponent } from './components/lv-service/lv-service.component';
import { LvStatusComponent } from './components/lv-status/lv-status.component';
import { MetaTagFormComponent } from './components/meta-tag-form/meta-tag-form.component';
import { MonacoEditorComponent } from './components/monaco-editor/monaco-editor.component';
import { OrderDetailsLicensesComponent } from './components/order-details-licenses/order-details-licenses.component';
import { OrderDetailsOrderItemsComponent } from './components/order-details-order-items/order-details-order-items.component';
import { OrderDetailsRefundsComponent } from './components/order-details-refunds/order-details-refunds.component';
import { OrderDetailsStripeRefundsComponent } from './components/order-details-stripe-refunds/order-details-stripe-refunds.component';
import { OrderDetailsComponent } from './components/order-details/order-details.component';
import { OrderListComponent } from './components/order-list/order-list.component';
import { OrderReceiptItemComponent } from './components/order-receipt-item/order-receipt-item.component';
import { OrderReceiptComponent } from './components/order-receipt/order-receipt.component';
import { OrganizationDropdownComponent } from './components/organization-dropdown/organization-dropdown.component';
import { OverlayToggleComponent } from './components/overlay-toggle/overlay-toggle.component';
import { OwnerSelectComponent } from './components/owner-select/owner-select.component';
import { PhoneNumberComponent } from './components/phone-number/phone-number.component';
import { ProductBundleComponent } from './components/product-bundle/product-bundle.component';
import { ProgressStatusComponent } from './components/progress-status/progress-status.component';
import { PromoCodeClaimComponent } from './components/promo-code-claim/promo-code-claim.component';
import { PromoCodeUseComponent } from './components/promo-code-uses/promo-code-uses.component';
import { QrCodeComponent } from './components/qr-code/qr-code.component';
import { RefundFormComponent } from './components/refund-form/refund-form.component';
import { RefundItemFormComponent } from './components/refund-item-form/refund-item-form.component';
import { ReportDashboardLinkComponent } from './components/report-dashboard-link/report-dashboard-link.component';
import { ReportStatDashboardLinkComponent } from './components/report-stat-dashboard-link/report-stat-dashboard-link.component';
import { RichTextEditorComponent } from './components/rich-text-editor/rich-text-editor.component';
import { RruleInputComponent } from './components/rrule-input/rrule-input.component';
import { SelectBookableClassForIlCourseComponent } from './components/select-bookable-class-for-il-course/select-bookable-class-for-il-course.component';
import { SessionDateComponent } from './components/session-date/session-date.component';
import { ShareDialogComponent } from './components/share-dialog/share-dialog.component';
import { ShippingInformationFormComponent } from './components/shipping-information-form/shipping-information-form.component';
import { ShippingInformationComponent } from './components/shipping-information/shipping-information.component';
import { ShoppingCartToggleComponent } from './components/shopping-cart-toggle/shopping-cart-toggle.component';
import { ShoppingCartComponent } from './components/shopping-cart/shopping-cart.component';
import { SidebarToggleComponent } from './components/sidebar-toggle/sidebar-toggle.component';
import { SkeletonCardFormComponent } from './components/skeleton-card-form/skeleton-card-form.component';
import { SkeletonCodeComponent } from './components/skeleton-code/skeleton-code.component';
import { SkeletonFieldComponent } from './components/skeleton-field/skeleton-field.component';
import { SkeletonLinesComponent } from './components/skeleton-lines/skeleton-lines.component';
import { SkeletonPostComponent } from './components/skeleton-post/skeleton-post.component';
import { SkeletonRectComponent } from './components/skeleton-rect/skeleton-rect.component';
import { SkeletonWordsComponent } from './components/skeleton-words/skeleton-words.component';
import { SocialShareComponent } from './components/social-share/social-share.component';
import { StorefrontCommComponent } from './components/storefront-comm/storefront-comm.component';
import { StorefrontTileComponent } from './components/storefront-tile/storefront-tile.component';
import { StripePaymentComponent } from './components/stripe-payment/stripe-payment.component';
import { TableEmptyMessageComponent } from './components/table-empty-message/table-empty-message.component';
import { TermsOfServiceAgreementsComponent } from './components/terms-of-service-agreements/terms-of-service-agreements.component';
import { ThumbnailListComponent } from './components/thumbnail-list/thumbnail-list.component';
import { TileCardComponent } from './components/tile-card/tile-card.component';
import { ToggleStatusComponent } from './components/toggle-status/toggle-status.component';
import { UpdateNodeArchivedComponent } from './components/update-node-archived/update-node-archived.component';
import { UserContactsComponent } from './components/user-contacts/user-contacts.component';
import { UserSearchComponent } from './components/user-search/user-search.component';
import { UserUpdateComponent } from './components/user-update/user-update.component';
import { ValidationErrorsComponent } from './components/validation-errors/validation-errors.component';
import { ValueTagComponent } from './components/value-tag/value-tag.component';
import { VerifiableCredentialWizardComponent } from './components/verifiable-credential-wizard/verifiable-credential-wizard.component';
import { VerifiedCredentialComponent } from './components/verified-credential/verified-credential.component';
import { VerifiedPersonComponent } from './components/verified-person/verified-person.component';
import { YesNoComponent } from './components/yes-no/yes-no.component';
import { AnalyticsEventDirective } from './directives/analytics-event.directive';
import { DataViewSearchDirective } from './directives/data-view-search.directive';
import { DataViewSortFixDirective } from './directives/data-view-sort-fix.directive';
import { DisableControlDirective } from './directives/disable-control.directive';
import { EqualToDirective } from './directives/equal-to.directive';
import { FormDirective } from './directives/form.directive';
import { NavigateDirective } from './directives/navigate.directive';
import { OpenLinksInNewWindowDirective } from './directives/open-links-in-new-window.directive';
import { PlaceholderDirective } from './directives/placeholder.directive';
import { PrintDirective } from './directives/print.directive';
import { QueryParamInputDirective } from './directives/query-param-input.directive';
import { RequiredIfDirective } from './directives/required-if.directive';
import { StopPropagationDirective } from './directives/stop-propagation.directive';
import { TableSearchDirective } from './directives/table-search.directive';
import { ToggleButtonActionsMenuItemDirective } from './directives/toggle-button-actions-menu-item.directive';
import { TrackProgressDirective } from './directives/track-progress.directive';
import { TrueValuesDirective } from './directives/true-values.directive';
import { AtPipe } from './pipes/at.pipe';
import { BooleanPipe } from './pipes/boolean.pipe';
import { CanPipe } from './pipes/can.pipe';
import { CategoryFilterPipe } from './pipes/category-filter.pipe';
import { ContactPipe } from './pipes/contact.pipe';
import { CoursePipe } from './pipes/course.pipe';
import { CurrencyWithCodePipe } from './pipes/currency-with-code.pipe';
import { DebugPipe } from './pipes/debug.pipe';
import { DigPipe } from './pipes/dig.pipe';
import { DiscountedPricePipe } from './pipes/discounted-price.pipe';
import { SafeHTMLPipe, SafeURLPipe } from './pipes/dom-safety.pipe';
import { DropdownOptionsPipe } from './pipes/dropdown-options.pipe';
import { DupPipe } from './pipes/dup.pipe';
import { DurationPipe } from './pipes/duration.pipe';
import { FaSpinPipe } from './pipes/fa-spin.pipe';
import { FilenamePipe } from './pipes/filename.pipe';
import { FilesizePipe } from './pipes/filesize.pipe';
import { GoogleViewerPipe } from './pipes/google-viewer.pipe';
import { GroupILClassPipe } from './pipes/group-il-class.pipe';
import { HumanizePipe } from './pipes/humanize.pipe';
import { ILLicensesPipe } from './pipes/il-licenses.pipe';
import { InfinityPipe } from './pipes/infinity.pipe';
import { InstructorOptionsPipe } from './pipes/instructor-options.pipe';
import { IsStatePipe } from './pipes/is-state.pipe';
import { IsStatusPipe } from './pipes/is-status.pipe';
import { LatencyPipe } from './pipes/latency.pipe';
import { LicenseSeatsPipe } from './pipes/license-seats.pipe';
import { LocationPipe } from './pipes/location.pipe';
import { MITPipe } from './pipes/mit.pipe';
import { NumericOptionsPipe } from './pipes/numeric-options.pipe';
import { OrderPipe } from './pipes/order.pipe';
import { OwnersToOptionsPipe } from './pipes/owners-to-options.pipe';
import { PhonePipe } from './pipes/phone.pipe';
import { PluralatePipe } from './pipes/pluralate.pipe';
import { PromoCodeAmountPipe } from './pipes/promo-code-amount.pipe';
import { RefundAmountPipe } from './pipes/refund-amount.pipe';
import { RelativeTimePipe } from './pipes/relative-time.pipe';
import { ScoreablePipe } from './pipes/scoreable.pipe';
import { SortByPipe } from './pipes/sort-by.pipe';
import { StringOptionsPipe } from './pipes/string-options.pipe';
import { TotalQuantityPipe } from './pipes/total-quantity.pipe';
import { UiPipe } from './pipes/ui.pipe';
import { UrlPipe } from './pipes/url.pipe';

@NgModule({
  declarations: [
    AblyChannelSubscriptionComponent,
    ActionsMenuComponent,
    ActionsMenuItemComponent,
    AddLearnersStepComponent,
    AddLearnersWizardComponent,
    AddressComponent,
    AddressFormComponent,
    AnalyticsEventDirective,
    AppDistractorComponent,
    AppStoreLinksComponent,
    AssignTrainingComponent,
    AssignTrainingWizardComponent,
    AtPipe,
    AvatarComponent,
    BadgeComponent,
    BatchActionsComponent,
    BlockableComponent,
    BooleanPipe,
    BulkDiscountFormComponent,
    CancelIlBookingComponent,
    CanPipe,
    CardTitleComponent,
    CartItemComponent,
    CategoryFilterPipe,
    CertificationCardComponent,
    CertificationChangeRequestComponent,
    CertificationDetailsComponent,
    CertificationExportComponent,
    CertificationListComponent,
    CheckoutBookableClassComponent,
    CheckoutBookableClassListComponent,
    CheckoutBookingCartItemComponent,
    CheckoutBookingComponent,
    CheckoutBookingCartItemComponent,
    CheckoutOrderComponent,
    CheckoutReceiptComponent,
    CheckoutWizardComponent,
    ClaimPromoCodeComponent,
    ColorPickerComponent,
    ConfirmButtonComponent,
    ConfirmLearnersTableComponent,
    ContactPipe,
    ContentHeaderComponent,
    CopyButtonComponent,
    CourseDeprecationBadgeComponent,
    CoursePipe,
    CurrencyWithCodePipe,
    DashboardFavouriteReportsComponent,
    DashboardTileComponent,
    DataViewSearchDirective,
    DataViewSortFixDirective,
    DateComponent,
    DebugPipe,
    DestroyNodeButtonComponent,
    DialogToggleComponent,
    DigPipe,
    DisableControlDirective,
    DiscountedPricePipe,
    DriverComponent,
    DropdownOptionsPipe,
    DupPipe,
    DurationPipe,
    EditButtonComponent,
    EditFormComponent,
    EmailAddressComponent,
    EmptyMessageComponent,
    EndIlClassButtonComponent,
    EqualToDirective,
    ExternalLinkComponent,
    FaSpinPipe,
    FeatureGateComponent,
    FileDisplayComponent,
    FileListComponent,
    FilesizePipe,
    FilenamePipe,
    FileUploadComponent,
    FlexContainerComponent,
    FloatedLabelFieldComponent,
    FormActionsComponent,
    FormComponent,
    FormDirective,
    FormFieldComponent,
    FormOptionComponent,
    FormSectionComponent,
    GoogleViewerPipe,
    GrantStatusComponent,
    GridComponent,
    GroupILClassPipe,
    HasManyPricesFormComponent,
    HelpTipComponent,
    HumanizePipe,
    IlBookingTableRowComponent,
    IlClassAvailabilityComponent,
    IlClassCourseItemsComponent,
    IlClassEnrolCardComponent,
    IlClassInstructorComponent,
    IlClassResultsSummaryComponent,
    IlClassSessionComponent,
    IlClassSessionsCellComponent,
    IlClassStatusComponent,
    IlCourseClassListComponent,
    ILLicensesPipe,
    IlSessionLocationComponent,
    InfinityPipe,
    InstructorOptionsPipe,
    InviteStatusComponent,
    IsStatePipe,
    IsStatusPipe,
    KeyValueFormComponent,
    LatencyPipe,
    LearnersImportComponent,
    LicenseSeatsPipe,
    LinkDocumentButtonComponent,
    ListFilterComponent,
    LocationPipe,
    LvServiceComponent,
    LvStatusComponent,
    MaxLearnersMessageComponent,
    MetaTagFormComponent,
    MITPipe,
    MonacoEditorComponent,
    NavigateDirective,
    NumericOptionsPipe,
    OpenLinksInNewWindowDirective,
    OrderDetailsComponent,
    OrderDetailsLicensesComponent,
    OrderDetailsOrderItemsComponent,
    OrderDetailsRefundsComponent,
    OrderDetailsStripeRefundsComponent,
    OrderReceiptComponent,
    OrderReceiptItemComponent,
    OrderListComponent,
    OrderPipe,
    OrganizationDropdownComponent,
    OverlayToggleComponent,
    OwnerSelectComponent,
    OwnersToOptionsPipe,
    PhoneNumberComponent,
    PhonePipe,
    PlaceholderDirective,
    PluralatePipe,
    PrintDirective,
    ProductBundleComponent,
    ProgressStatusComponent,
    PromoCodeAmountPipe,
    PromoCodeClaimComponent,
    PromoCodeUseComponent,
    QrCodeComponent,
    QueryParamInputDirective,
    RefundAmountPipe,
    RefundFormComponent,
    RefundItemFormComponent,
    RelativeTimePipe,
    ReportDashboardLinkComponent,
    ReportStatDashboardLinkComponent,
    RequiredIfDirective,
    RichTextEditorComponent,
    RruleInputComponent,
    SafeHTMLPipe,
    SafeURLPipe,
    ScoreablePipe,
    SelectBookableClassForIlCourseComponent,
    SessionDateComponent,
    ShareDialogComponent,
    ShippingInformationComponent,
    ShippingInformationFormComponent,
    ShoppingCartComponent,
    ShoppingCartToggleComponent,
    SidebarToggleComponent,
    SkeletonCardFormComponent,
    SkeletonCodeComponent,
    SkeletonFieldComponent,
    SkeletonLinesComponent,
    SkeletonPostComponent,
    SkeletonRectComponent,
    SkeletonWordsComponent,
    SocialShareComponent,
    SortByPipe,
    StopPropagationDirective,
    StorefrontCommComponent,
    StorefrontTileComponent,
    StringOptionsPipe,
    StripePaymentComponent,
    TableEmptyMessageComponent,
    TableSearchDirective,
    TermsOfServiceAgreementsComponent,
    ThumbnailListComponent,
    TileCardComponent,
    TimezoneSelectComponent,
    ToggleButtonActionsMenuItemDirective,
    ToggleStatusComponent,
    TotalQuantityPipe,
    TrackProgressDirective,
    TrueValuesDirective,
    UiPipe,
    UpdateNodeArchivedComponent,
    UrlPipe,
    UserContactsComponent,
    UserSearchComponent,
    ValidationErrorsComponent,
    ValueTagComponent,
    VerifiableCredentialsComponent,
    VerifiableCredentialWizardComponent,
    VerifiedCredentialComponent,
    VerifiedPersonComponent,
    UserUpdateComponent,
    YesNoComponent,
    CustomFieldComponent
  ],
  imports: [
    CommonModule,
    PrimeModule,
    FontAwesomeModule,
    ClipboardModule,
    TranslateModule.forChild(),
    ReactiveFormsModule,
    QRCodeModule
  ],
  exports: [
    AblyChannelSubscriptionComponent,
    ActionsMenuComponent,
    ActionsMenuItemComponent,
    AddLearnersStepComponent,
    AddLearnersWizardComponent,
    AddressComponent,
    AddressFormComponent,
    AnalyticsEventDirective,
    AppDistractorComponent,
    AppStoreLinksComponent,
    AssignTrainingWizardComponent,
    AtPipe,
    AvatarComponent,
    BadgeComponent,
    BatchActionsComponent,
    BlockableComponent,
    BooleanPipe,
    BulkDiscountFormComponent,
    CancelIlBookingComponent,
    CanPipe,
    CardTitleComponent,
    CartItemComponent,
    CategoryFilterPipe,
    CertificationCardComponent,
    CertificationChangeRequestComponent,
    CertificationDetailsComponent,
    CertificationExportComponent,
    CertificationListComponent,
    CheckoutBookableClassComponent,
    CheckoutBookableClassListComponent,
    CheckoutBookingCartItemComponent,
    CheckoutBookingComponent,
    CheckoutBookingCartItemComponent,
    CheckoutOrderComponent,
    CheckoutReceiptComponent,
    CheckoutWizardComponent,
    ClaimPromoCodeComponent,
    ClipboardModule,
    ColorPickerComponent,
    ConfirmButtonComponent,
    ConfirmLearnersTableComponent,
    ContactPipe,
    ContentHeaderComponent,
    CopyButtonComponent,
    CourseDeprecationBadgeComponent,
    CoursePipe,
    CurrencyWithCodePipe,
    DashboardFavouriteReportsComponent,
    DashboardTileComponent,
    DataViewSearchDirective,
    DataViewSortFixDirective,
    DateComponent,
    DebugPipe,
    DestroyNodeButtonComponent,
    DialogToggleComponent,
    DigPipe,
    DisableControlDirective,
    DiscountedPricePipe,
    DriverComponent,
    DropdownOptionsPipe,
    DupPipe,
    DurationPipe,
    EditButtonComponent,
    EditFormComponent,
    EmailAddressComponent,
    EmptyMessageComponent,
    EndIlClassButtonComponent,
    EqualToDirective,
    ExternalLinkComponent,
    FaSpinPipe,
    FeatureGateComponent,
    FileDisplayComponent,
    FileListComponent,
    FilesizePipe,
    FilenamePipe,
    FileUploadComponent,
    FlexContainerComponent,
    FloatedLabelFieldComponent,
    FontAwesomeModule,
    FormActionsComponent,
    FormComponent,
    FormDirective,
    FormFieldComponent,
    FormOptionComponent,
    FormSectionComponent,
    GoogleViewerPipe,
    GrantStatusComponent,
    GridComponent,
    GroupILClassPipe,
    HasManyPricesFormComponent,
    HelpTipComponent,
    HumanizePipe,
    IlClassAvailabilityComponent,
    IlBookingTableRowComponent,
    IlClassCourseItemsComponent,
    IlClassEnrolCardComponent,
    IlClassInstructorComponent,
    IlClassResultsSummaryComponent,
    IlClassSessionComponent,
    IlClassSessionsCellComponent,
    IlClassStatusComponent,
    IlCourseClassListComponent,
    ILLicensesPipe,
    IlSessionLocationComponent,
    InfinityPipe,
    InstructorOptionsPipe,
    InviteStatusComponent,
    IsStatePipe,
    IsStatusPipe,
    KeyValueFormComponent,
    LatencyPipe,
    LicenseSeatsPipe,
    LinkDocumentButtonComponent,
    ListFilterComponent,
    LocationPipe,
    LvServiceComponent,
    LvStatusComponent,
    MaxLearnersMessageComponent,
    MonacoEditorComponent,
    MetaTagFormComponent,
    MITPipe,
    NavigateDirective,
    NumericOptionsPipe,
    OpenLinksInNewWindowDirective,
    OrderDetailsComponent,
    OrderDetailsOrderItemsComponent,
    OrderDetailsLicensesComponent,
    OrderDetailsRefundsComponent,
    OrderDetailsStripeRefundsComponent,
    OrderReceiptComponent,
    OrderReceiptItemComponent,
    OrderListComponent,
    OrderPipe,
    OrganizationDropdownComponent,
    OverlayToggleComponent,
    OwnerSelectComponent,
    OwnersToOptionsPipe,
    PhoneNumberComponent,
    PhonePipe,
    PlaceholderDirective,
    PluralatePipe,
    PrimeModule,
    PrintDirective,
    ProductBundleComponent,
    ProgressStatusComponent,
    PromoCodeAmountPipe,
    PromoCodeClaimComponent,
    PromoCodeUseComponent,
    QrCodeComponent,
    QueryParamInputDirective,
    RefundAmountPipe,
    RefundFormComponent,
    RefundItemFormComponent,
    RelativeTimePipe,
    ReportDashboardLinkComponent,
    ReportStatDashboardLinkComponent,
    RequiredIfDirective,
    RichTextEditorComponent,
    RruleInputComponent,
    SafeHTMLPipe,
    SafeURLPipe,
    ScoreablePipe,
    SelectBookableClassForIlCourseComponent,
    SessionDateComponent,
    ShareDialogComponent,
    ShippingInformationComponent,
    ShoppingCartComponent,
    ShoppingCartToggleComponent,
    SidebarToggleComponent,
    SkeletonCardFormComponent,
    SkeletonCodeComponent,
    SkeletonFieldComponent,
    SkeletonLinesComponent,
    SkeletonPostComponent,
    SkeletonRectComponent,
    SkeletonWordsComponent,
    SocialShareComponent,
    SortByPipe,
    StopPropagationDirective,
    StorefrontCommComponent,
    StorefrontTileComponent,
    StringOptionsPipe,
    TableEmptyMessageComponent,
    TableSearchDirective,
    TermsOfServiceAgreementsComponent,
    ThumbnailListComponent,
    TileCardComponent,
    TimezoneSelectComponent,
    ToggleButtonActionsMenuItemDirective,
    ToggleStatusComponent,
    TotalQuantityPipe,
    TrackProgressDirective,
    TrueValuesDirective,
    UiPipe,
    UpdateNodeArchivedComponent,
    UrlPipe,
    UserContactsComponent,
    UserSearchComponent,
    ValidationErrorsComponent,
    ValueTagComponent,
    VerifiableCredentialsComponent,
    VerifiableCredentialWizardComponent,
    VerifiedCredentialComponent,
    VerifiedPersonComponent,
    YesNoComponent,
    CustomFieldComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SharedModule {}
